
/**
 * Utility to track failed movie requests and prevent repeated API calls
 */

// In-memory cache of movie IDs that failed to load
const failedMovieIds = new Set<number>();

// Check if sessionStorage is available (not in SSR)
const hasSessionStorage = typeof sessionStorage !== 'undefined';

// Load any previously failed IDs from sessionStorage
try {
  if (hasSessionStorage) {
    const cachedIds = sessionStorage.getItem('failedMovieIds');
    if (cachedIds) {
      const ids = JSON.parse(cachedIds) as number[];
      ids.forEach(id => failedMovieIds.add(id));
    }
  }
} catch (e) {
  console.warn('Failed to load cached movie IDs from sessionStorage', e);
}

/**
 * Custom error class for movie not found errors
 */
export class MovieNotFoundError extends Error {
  constructor(tmdbId: number) {
    super(`Movie with TMDB ID ${tmdbId} not found`);
    this.name = 'MovieNotFoundError';
  }
}

/**
 * Add a movie ID to the failed requests cache
 */
export function addFailedMovieId(tmdbId: number): void {
  failedMovieIds.add(tmdbId);
  
  // Persist to sessionStorage if available
  if (hasSessionStorage) {
    try {
      sessionStorage.setItem(
        'failedMovieIds', 
        JSON.stringify(Array.from(failedMovieIds))
      );
    } catch (e) {
      console.warn('Failed to save failed movie IDs to sessionStorage', e);
    }
  }
}

/**
 * Check if a movie ID is known to fail
 */
export function isKnownFailedMovie(tmdbId: number): boolean {
  return failedMovieIds.has(tmdbId);
}

/**
 * Clear the failed requests cache
 */
export function clearFailedMoviesCache(): void {
  failedMovieIds.clear();
  
  if (hasSessionStorage) {
    try {
      sessionStorage.removeItem('failedMovieIds');
    } catch (e) {
      console.warn('Failed to clear failed movie IDs from sessionStorage', e);
    }
  }
}
